import React from "react";
import { useNavigate } from "react-router-dom";
import { Wrapper } from "components/core/Header/styles.header";
import BackButton from "../Button/BackButton";
import { Loader } from "../Common/Loader";
import ShowToast from "../Common/Toast";
import TransactionDetailSheet from "./TransactionDetailSheet";
import {
  ChevronLeftIcon,
  SearchIcon,
  DownloadIcon,
  DateRangeChip,
  PaymentRow,
  TransactionRow,
} from "./PayoutElements";
import { PAYOUTS_TABS } from "./config";
import { MobilePageTabsBar } from "components/shared/PageTabs/PageTabs";
import {
  Screen,
  FilterRow,
  FilterChip,
  ListHeader,
  CountText,
  AdviceLink,
  EmptyState,
  ScrollList,
  HeaderGroup,
  HeaderBackWrap,
  HeaderText,
  HeaderTitle,
  HeaderSubtitle,
  HeaderIconButton,
  SearchBar,
  SearchBackButton,
  SearchField,
  SearchInput,
  TabBarWrapper,
} from "./styles.payouts.js";

const PAYOUTS_PAGE_CONFIG = [
  { key: PAYOUTS_TABS.PAYMENTS, label: "Payments" },
  { key: PAYOUTS_TABS.TRANSACTIONS, label: "Transactions" },
];

const PayoutMobile = ({
  isPayments,
  vendorName,
  statusFilterOptions,
  downloading,
  downloadError,
  setDownloadError,
  handleDownload,
  mobileActiveFilter,
  toggleMobileFilter,
  removeMobileFilter,
  mobileDueRange,
  setMobileDueRange,
  mobileSearchOpen,
  setMobileSearchOpen,
  mobileSearchDraft,
  setMobileSearchDraft,
  mobileSearchInputRef,
  closeMobileSearch,
  handleMobileSearchSubmit,
  switchMobileTab,
  handleMobileScroll,
  mobileScrollRef,
  copyUtr,
  copyToast,
  mobileItems,
  mobileCountLabel,
  isLoading,
  selectedMobileTransaction,
  setSelectedMobileTransaction,
}) => {
  const navigate = useNavigate();

  const handleBackClick = () => navigate("/");

  const handlePaymentClick = (referenceNumber) =>
    navigate(
      `/payouts/details?reference_number=${encodeURIComponent(referenceNumber)}`
    );

  return (
    <React.Fragment>
      <Wrapper
        className="po-header"
        style={{
          background: "#F4F6FB",
          height: "auto",
          minHeight: 0,
          padding: "12px 16px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 16,
        }}
      >
        {mobileSearchOpen && !isPayments ? (
          <SearchBar onSubmit={handleMobileSearchSubmit}>
            <SearchField>
              <SearchBackButton
                type="button"
                aria-label="Close search"
                onClick={closeMobileSearch}
              >
                <ChevronLeftIcon />
              </SearchBackButton>
              <SearchInput
                type="search"
                enterKeyHint="search"
                placeholder="Search Transaction Number"
                value={mobileSearchDraft}
                onChange={(e) => setMobileSearchDraft(e.target.value)}
                ref={mobileSearchInputRef}
              />
            </SearchField>
          </SearchBar>
        ) : (
          <>
            <HeaderGroup>
              <HeaderBackWrap onClick={handleBackClick}>
                <BackButton />
              </HeaderBackWrap>
              <HeaderText>
                <HeaderTitle>Payments</HeaderTitle>
                {vendorName ? <HeaderSubtitle>{vendorName}</HeaderSubtitle> : null}
              </HeaderText>
            </HeaderGroup>
            {!isPayments ? (
              <HeaderIconButton
                type="button"
                aria-label="Search"
                onClick={() => setMobileSearchOpen(true)}
              >
                <SearchIcon />
              </HeaderIconButton>
            ) : null}
          </>
        )}
      </Wrapper>
      <Screen>
        <TabBarWrapper>
          <MobilePageTabsBar
            tabs={PAYOUTS_PAGE_CONFIG}
            activeKey={isPayments ? PAYOUTS_TABS.PAYMENTS : PAYOUTS_TABS.TRANSACTIONS}
            onChange={switchMobileTab}
            ariaLabel="Payout pages"
          />
        </TabBarWrapper>

        <FilterRow>
          <DateRangeChip
            from={mobileDueRange.from}
            to={mobileDueRange.to}
            onApply={setMobileDueRange}
          />
          {statusFilterOptions.map((f) => {
            const selected = mobileActiveFilter.includes(f.key);
            return (
              <FilterChip
                key={f.key}
                active={selected}
                onClick={() => !selected && toggleMobileFilter(f.key)}
              >
                {f.label}
                {selected ? (
                  <span
                    role="button"
                    aria-label={`Remove ${f.label} filter`}
                    onClick={(e) => removeMobileFilter(f.key, e)}
                    style={{ marginLeft: 2, cursor: "pointer", fontSize: 10 }}
                  >
                    ✕
                  </span>
                ) : null}
              </FilterChip>
            );
          })}
        </FilterRow>

        <ListHeader>
          <CountText>{mobileCountLabel}</CountText>
          <AdviceLink
            type="button"
            onClick={handleDownload}
            disabled={downloading}
          >
            <span>Payment advice</span>
            <DownloadIcon />
          </AdviceLink>
        </ListHeader>

        <ScrollList ref={mobileScrollRef} onScroll={handleMobileScroll}>
          {isLoading && !mobileItems?.length ? (
            <Loader />
          ) : mobileItems?.length ? (
            mobileItems.map((item, idx) =>
              isPayments ? (
                <PaymentRow
                  key={item.reference_number || idx}
                  item={item}
                  onCopyUtr={copyUtr}
                  onClick={
                    item.reference_number
                      ? () => handlePaymentClick(item.reference_number)
                      : undefined
                  }
                />
              ) : (
                <TransactionRow
                  key={item.transaction_number || idx}
                  item={item}
                  onClick={() => setSelectedMobileTransaction(item)}
                />
              )
            )
          ) : (
            <EmptyState>
              No {isPayments ? "payments" : "transactions"} to show
            </EmptyState>
          )}
          {isLoading && mobileItems?.length ? <Loader /> : null}
        </ScrollList>
      </Screen>
      {selectedMobileTransaction ? (
        <TransactionDetailSheet
          item={selectedMobileTransaction}
          onClose={() => setSelectedMobileTransaction(null)}
        />
      ) : null}
      {copyToast}
      {downloadError ? (
        <ShowToast
          type="error"
          errorMsg={downloadError}
          onClose={() => setDownloadError("")}
        />
      ) : null}
    </React.Fragment>
  );
};

export default PayoutMobile;
